<template>
  <div>
    <template v-if="chartAxisConfig?.singleSimulation">
      <input-dropdown
        v-slot="{ item }"
        :searchable="true"
        :options="allSimulations"
        :order="simulationsOrder"
        :value="selectedSimulation"
        @update:value="selectSimulation($event.id)"
      >
        {{ item.title }}
      </input-dropdown>
    </template>
    <template v-else>
      <!-- Marker for ALL simulations option -->
      <button-item
        v-if="dataToggles.all.simulation"
        class="mb-4"
        :icons="['x']"
        @click:x="clearSimulations()"
      >
        {{ $t("labels.allSimulations") }}
      </button-item>

      <button-list
        :items="allSimulations"
        :order="simulationOrder"
        prop="title"
        :sortable="true"
        :selected-item="
          chartAxisConfig?.singleSimulation ? selectedSimulation : undefined
        "
        @update:order="updateSimulationsOrder($event)"
      >
        <template #icons="{ item: simulation }">
          <button
            v-if="
              boundToActiveSimulation == false ||
              simulation.id != openSimulation?.id
            "
            type="button"
            class="button-icon button-item__icon"
            @click="removeSimulation(simulation.id)"
          >
            <lucide-icon name="x" />
          </button>
        </template>
        <template #after="{ item: simulation }">
          <design-button
            v-if="chartAxisConfig?.hasSimulationSymbols"
            :symbol="itemConfigs?.simulation?.[simulation.id]?.symbol"
            type="symbol"
            :filled="true"
            @update:symbol="setSimulationSymbol(simulation.id, $event)"
          />
          <design-button
            v-if="chartAxisConfig?.hasSimulationColors"
            :color="itemConfigs?.simulation?.[simulation.id]?.color"
            type="color"
            button-style="flat"
            :filled="true"
            @update:color="
              setSimulationColor(simulation.id, $event as string)
            "
          />
        </template>
        <template v-if="!dataToggles.all.simulation" #after-list>
          <input-dropdown
            :options="unchosenSimulations"
            prop="title"
            :clear-on-select="true"
            :searchable="true"
            :placeholder="$t('actions.addSimulation')"
            @update:value="addSimulation($event.id)"
          >
            <template
              v-if="!dataToggles.all.simulation"
              #after-options="{ close }"
            >
              <button
                type="button"
                class="options-list__option"
                @click="
                  close();
                  toggleAllOption('simulation');
                "
              >
                {{ $t("labels.allSimulations") }}
              </button>
            </template>
            <template #empty>
              <!-- This list is never empty. -->
              <span></span>
            </template>
          </input-dropdown>
        </template>
      </button-list>
    </template>

    <div v-if="selectedSimulations.length > 0" class="mt-2">
      <h3 class="label mt-0">{{ $t("labels.resultRange") }}</h3>
      <ChartRoundChooser :chart-uid="chart.uid" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useEvaluationStore } from '../evaluation/evaluations.store';
import { useChartData } from './useChartData';
import { useChartStore } from './charts.store';
import { useSimulationStore } from '../simulation/simulations.store';
import { useSimulationViewStore } from '@editor/views/simulation/simulationView.store';

import InputDropdown from '@core/input/InputDropdown.vue';
import ButtonItem from '@core/input/ButtonItem.vue';
import ButtonList from '@core/ui/ButtonList.vue';
import DesignButton from '@core/input/DesignButton.vue';
import LucideIcon from '@core/ui/LucideIcon.vue';
import ChartRoundChooser from './ChartBuilderRoundChooser.vue';

import type { Uid } from '@/helpers/utility/Id';
import type { ConfigurableType } from './ChartItem';
import type { UnfilledSymbolName } from '@core/ui/svg-icons/symbols/SymbolName';

const props = defineProps<{
  chartUid: Uid<"evaluation" | "chart">;
}>();

const { t } = useI18n();
const simulationStore = useSimulationStore();

const store = computed(() => {
  if (props.chartUid.startsWith("evaluation")) {
    return useEvaluationStore();
  }
  else if (props.chartUid.startsWith("chart")) {
    return useChartStore();
  }
  throw new Error("Invalid chart UID");
});

const chart = computed(() => {
  return store.value.byUid[props.chartUid];
});

const {
  dataToggles,
  chartAxisConfig,
  itemConfigs,
  simulationOrder,
  selectedSimulations,
  unchosenSimulations,
  selectedSimulation,
} = useChartData(chart);

// ========== SIMULATION STORE ==========

const allSimulations = computed(() => {
  if (boundToActiveSimulation.value === true) {
    return {
      ...simulationStore.items
    };
  }

  return {
    [-1]: {
      id: -1,
      uid: "simulation_-1",
      title: t("labels.openSimulation")
    },
    ...simulationStore.items
  };
});

const simulationsOrder = computed(() => {
  return [-1, ...simulationStore.order];
});

// ========== ADDING & REMOVING ==========

function addSimulation(simulationId: number) {
  store.value.addSimulation(chart.value.id, simulationId);
}
function removeSimulation(simulationId: number) {
  store.value.removeSimulations(chart.value.id, [simulationId]);
}
function clearSimulations() {
  store.value.clearSimulations(chart.value.id);
}
function updateSimulationsOrder(newOrder: number[]) {
  return store.value.updateItemOrder(
    chart.value.id,
    "simulation",
    newOrder
  );
}

// ========== OPEN SIMULATION ==========

const boundToActiveSimulation = computed(() => {
  return (
    "isDefaultSimulation" in chart.value &&
    chart.value.isDefaultSimulation
  );
});

const openSimulation = computed(() => {
  return useSimulationViewStore().simulation;
});

function selectSimulation(simulationId: number | null) {
  if (boundToActiveSimulation.value === true) {
    selectOpenSimulation(simulationId);
  } else {
    store.value.selectSimulation(chart.value.id, simulationId);
  }
}
function selectOpenSimulation(simulationId: number | null) {
  useSimulationViewStore().loadSimulation({ simulationId: simulationId });
}

// ========== SETTERS ==========

function setSimulationSymbol(simulationId: number, symbol: UnfilledSymbolName) {
  store.value.setItemSymbol(
    chart.value.id,
    "simulation",
    simulationId,
    symbol
  );
}
function setSimulationColor(simulationId: number, color: string) {
  setItemColor(
    "simulation",
    simulationId,
    color
  );
}

function setItemColor(
  itemType: ConfigurableType,
  itemId: number | string,
  color: string | null,
  simulationId?: number,
  subgroup?: string
) {
  store.value.setItemColor(
    chart.value.id,
    itemType,
    itemId,
    color,
    simulationId,
    subgroup
  );
}

function toggleAllOption(type: "simulation" | "resource" | "target" | "index") {
  store.value.toggleAllOption(chart.value.id, type);
}
</script>